import InputSlider from '@/components/sections/builder/settings/input-slider';
import InputTab from '@/components/sections/builder/settings/input-tab';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ThemeAnimationType, useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';

export const DEFAULT_THEME_ANIMATION: ThemeAnimationType = {
  animateOnScroll: false,
  animationDelay: 200,
  animationType: 'fade',
  hoverEffect: 'none',
};

export default function ThemeAnimationSetting() {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const animation = builder.theme?.animation || DEFAULT_THEME_ANIMATION;

  const setValue = (value: Partial<ThemeAnimationType>) => {
    builder.setTheme({
      animation: { ...animation, ...value },
    });
  };

  const animationTypeOptions = [
    { value: 'fade', label: 'fade' },
    { value: 'fade-up', label: 'fade-up' },
    { value: 'fade-down', label: 'fade-down' },
    { value: 'fade-left', label: 'fade-left' },
    { value: 'fade-right', label: 'fade-right' },
    { value: 'fade-up-right', label: 'fade-up-right' },
    { value: 'fade-up-left', label: 'fade-up-left' },
    { value: 'fade-down-right', label: 'fade-down-right' },
    { value: 'fade-down-left', label: 'fade-down-left' },
    { value: 'flip-up', label: 'flip-up' },
    { value: 'flip-down', label: 'flip-down' },
    { value: 'flip-left', label: 'flip-left' },
    { value: 'flip-right', label: 'flip-right' },
    { value: 'slide-up', label: 'slide-up' },
    { value: 'slide-down', label: 'slide-down' },
    { value: 'slide-left', label: 'slide-left' },
    { value: 'slide-right', label: 'slide-right' },
    { value: 'zoom-in', label: 'zoom-in' },
    { value: 'zoom-in-up', label: 'zoom-in-up' },
    { value: 'zoom-in-down', label: 'zoom-in-down' },
    { value: 'zoom-in-left', label: 'zoom-in-left' },
    { value: 'zoom-in-right', label: 'zoom-in-right' },
    { value: 'zoom-out', label: 'zoom-out' },
    { value: 'zoom-out-up', label: 'zoom-out-up' },
    { value: 'zoom-out-down', label: 'zoom-out-down' },
    { value: 'zoom-out-left', label: 'zoom-out-left' },
    { value: 'zoom-out-right', label: 'zoom-out-right' },
  ];

  return (
    <div>
      <div className='flex gap-x-1'>
        <Checkbox
          id='animateOnScroll'
          aria-label='Select'
          className='mx-2'
          checked={animation.animateOnScroll}
          onCheckedChange={(checked) => {
            setValue({ animateOnScroll: !!checked });
          }}
        />
        <label htmlFor='animateOnScroll'>Reveal sections on scroll</label>
      </div>
      {animation.animateOnScroll && (
        <>
          <div className='mb-4 mt-4'>Animation type</div>
          <div className='mb-4'>
            <Select
              name='animationType'
              value={animation.animationType}
              onValueChange={(val) => setValue({ animationType: val })}
            >
              <SelectTrigger className='py-5'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent side='bottom' className='max-h-48 overflow-y-auto'>
                {animationTypeOptions.map((obj) => (
                  <SelectItem key={obj.value} value={obj.value}>
                    {obj.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <InputSlider
            label='Animation delay'
            value={animation.animationDelay || 0}
            max={600}
            min={0}
            step={100}
            onChange={(val) => setValue({ animationDelay: val })}
            rightContent={<div>ms</div>}
          />
        </>
      )}
      <div className='mb-4 mt-4 font-bold'>Hover effect</div>
      <InputTab
        grid={2}
        defaultValue={animation.hoverEffect}
        options={[
          { label: 'None', value: 'none' },
          { label: 'Vertical Lift', value: 'lift-up' },
          // { label: '3D Lift', value: 'lift-3d' },
        ]}
        onChange={(v) => setValue({ hoverEffect: v })}
      />
      <div className='mt-3'>Affects cards and buttons.</div>
    </div>
  );
}
